import { useEffect, useRef, useState, type FormEvent } from 'react';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import { Link } from '@tanstack/react-router';
import { track } from '@/lib/analytics';
import {
  DIVORCE_JURISDICTIONS,
  getDivorceJurisdictionByCode,
  isPacketState,
} from '@/lib/divorce';
import { DOC_PREP_DISCLAIMER, DV_SCREEN_HINT, DV_SCREEN_QUESTION } from '@/lib/divorce-legal';
import type { DivorceAnswers } from '@/routes/divorce/interview';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

export type FitCheckGate = {
  status: 'eligible' | 'blocked' | 'wait';
  blocked_category?: string | null;
  reasons: { code: string; message: string; resource_url?: string | null }[];
};

export type FitCheckSaveResult = {
  answers: DivorceAnswers;
  eligibility: FitCheckGate | null;
  email_attached: boolean;
};

type Screen =
  | 'intro'
  | 'state_select'
  | 'state_unsupported'
  | 'annulment'
  | 'safety'
  | 'sign_together'
  | 'agreement'
  | 'bankruptcy'
  | 'pending_cases'
  | 'prior_marriage'
  | 'pregnant'
  | 'outcome';

const QUESTION_ORDER: Screen[] = [
  'annulment',
  'safety',
  'sign_together',
  'agreement',
  'bankruptcy',
  'pending_cases',
  'prior_marriage',
  'pregnant',
];

function withEligibility(answers: DivorceAnswers, patch: Record<string, boolean>): DivorceAnswers {
  return {
    ...answers,
    eligibility: { ...(answers.eligibility ?? {}), ...patch },
  };
}

function applyAnswer(answers: DivorceAnswers, screen: Screen, yes: boolean): DivorceAnswers {
  switch (screen) {
    case 'annulment':
      return withEligibility(answers, { wants_annulment: !yes });
    case 'safety':
      return withEligibility(answers, { dv_flag: !yes });
    case 'sign_together':
      return {
        ...answers,
        filing_mode: yes ? 'joint' : 'sole',
        service: { ...answers.service, spouse_will_sign_waiver: yes },
      };
    case 'agreement':
      return withEligibility(answers, { contested: !yes });
    case 'bankruptcy':
      return withEligibility(answers, { active_bankruptcy: yes });
    case 'pending_cases':
      return withEligibility(answers, { pending_cases: yes });
    case 'prior_marriage':
      return withEligibility(answers, { prior_marriage_not_final: !yes });
    case 'pregnant':
      return withEligibility(answers, { pregnant: yes });
    default:
      return answers;
  }
}

function firstUnansweredScreen(answers: DivorceAnswers, gate?: FitCheckGate | null): Screen {
  const e = answers.eligibility ?? {};
  const started = e.wants_annulment !== undefined || answers.filing_mode === 'sole';
  if (started && gate && gate.status !== 'eligible') return 'outcome';
  if (e.wants_annulment === undefined) return 'intro';
  if (e.contested === undefined && e.active_bankruptcy === undefined && e.pending_cases === undefined) {
    return 'safety';
  }
  if (e.contested === undefined) return 'agreement';
  if (e.active_bankruptcy === undefined) return 'bankruptcy';
  if (e.pending_cases === undefined) return 'pending_cases';
  if (e.prior_marriage_not_final === undefined) return 'prior_marriage';
  if (e.pregnant === undefined) return 'pregnant';
  return 'outcome';
}

function outcomeLabel(gate: FitCheckGate | null): 'may_fit' | 'wait' | 'needs_help' {
  if (gate?.status === 'wait') return 'wait';
  if (gate?.status === 'blocked') return 'needs_help';
  return 'may_fit';
}

export function DivorceFitCheck({
  answers,
  gate,
  saving,
  emailAttached,
  jurisdictionCode,
  initialStateCode,
  onSave,
  onSelectState,
  onContinue,
  onAttachEmail,
}: {
  answers: DivorceAnswers;
  gate: FitCheckGate | null;
  saving: boolean;
  emailAttached: boolean;
  jurisdictionCode: string;
  initialStateCode?: string;
  onSave: (next: DivorceAnswers) => Promise<FitCheckSaveResult | null>;
  onSelectState: (code: string) => Promise<FitCheckSaveResult | null>;
  onContinue: () => void;
  onAttachEmail: (email: string) => Promise<boolean>;
}) {
  const [screen, setScreen] = useState<Screen>(() => firstUnansweredScreen(answers, gate));
  const [busy, setBusy] = useState(false);
  // Whether this session includes the state question (affects the counter).
  const [stateAsked] = useState(() => !isPacketState(jurisdictionCode));
  const viewedRef = useRef(false);
  const startedRef = useRef(false);
  const completedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    track('Divorce Fit Check Viewed', jurisdictionCode ? { state: jurisdictionCode } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markCompleted = (nextGate: FitCheckGate | null) => {
    if (completedRef.current || !nextGate) return;
    completedRef.current = true;
    track('Divorce Fit Check Completed', {
      outcome: outcomeLabel(nextGate),
      ...(jurisdictionCode ? { state: jurisdictionCode } : {}),
      ...(nextGate.status === 'blocked' && nextGate.blocked_category
        ? { blocked_category: nextGate.blocked_category }
        : {}),
    });
  };

  const stateOffset = stateAsked ? 1 : 0;
  const progressTotal = QUESTION_ORDER.length + stateOffset;
  const questionIndex = QUESTION_ORDER.indexOf(screen);
  const progressCurrent =
    screen === 'state_select'
      ? 1
      : questionIndex >= 0
        ? questionIndex + 1 + stateOffset
        : progressTotal;
  const showProgress =
    screen !== 'intro' && screen !== 'outcome' && screen !== 'state_unsupported';

  const finishIfGated = (nextGate: FitCheckGate | null, lastScreen: Screen) => {
    if (nextGate && nextGate.status !== 'eligible') {
      markCompleted(nextGate);
      setScreen('outcome');
      return;
    }
    const idx = QUESTION_ORDER.indexOf(lastScreen);
    if (idx >= 0 && idx < QUESTION_ORDER.length - 1) {
      setScreen(QUESTION_ORDER[idx + 1]);
      return;
    }
    markCompleted(nextGate);
    setScreen('outcome');
  };

  const markStarted = (stateCode?: string) => {
    if (startedRef.current) return;
    startedRef.current = true;
    track('Divorce Fit Check Started', stateCode ? { state: stateCode } : undefined);
  };

  const answerQuestion = async (yes: boolean) => {
    if (busy || QUESTION_ORDER.indexOf(screen) < 0) return;
    markStarted(jurisdictionCode || undefined);
    setBusy(true);
    try {
      const next = applyAnswer(answers, screen, yes);
      const saved = await onSave(next);
      finishIfGated(saved?.eligibility ?? gate, screen);
    } finally {
      setBusy(false);
    }
  };

  const selectState = async (code: string) => {
    if (busy || !code) return;
    markStarted(code);
    setBusy(true);
    try {
      const supported = isPacketState(code);
      track('Divorce Fit Check State Selected', { state: code, supported });
      await onSelectState(code);
      setScreen(supported ? 'annulment' : 'state_unsupported');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='mx-auto max-w-xl px-4 py-10'>
      <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8'>
        {showProgress ? (
          <p className='mb-5 text-sm font-medium text-gray-500' style={nunito}>
            Question {progressCurrent} of {progressTotal}
          </p>
        ) : null}

        {screen === 'intro' ? (
          <IntroScreen
            onStart={() =>
              setScreen(isPacketState(jurisdictionCode) ? 'annulment' : 'state_select')
            }
          />
        ) : null}

        {screen === 'state_select' ? (
          <StateSelectScreen
            initialCode={initialStateCode}
            busy={busy || saving}
            onSelect={selectState}
          />
        ) : null}

        {screen === 'state_unsupported' ? (
          <StateUnsupportedScreen
            stateCode={jurisdictionCode || initialStateCode || ''}
            emailAttached={emailAttached}
            onAttachEmail={onAttachEmail}
          />
        ) : null}

        {screen === 'annulment' ? (
          <QuestionScreen
            label='Do you want a divorce, not an annulment?'
            hint='Annulment is a different court process. Harbor prepares divorce forms only.'
            busy={busy || saving}
            onAnswer={answerQuestion}
          />
        ) : null}

        {screen === 'safety' ? (
          <QuestionScreen
            label={DV_SCREEN_QUESTION}
            hint={DV_SCREEN_HINT}
            busy={busy || saving}
            onAnswer={answerQuestion}
          />
        ) : null}

        {screen === 'sign_together' ? (
          <QuestionScreen
            label='Are both of you willing to sign the papers together?'
            hint='California packet prep is the joint petition. Both of you sign the same forms.'
            busy={busy || saving}
            onAnswer={answerQuestion}
          />
        ) : null}

        {screen === 'agreement' ? (
          <QuestionScreen
            label='Are you working toward agreement on the major terms?'
            hint='Property, debts, support, and (if you have kids) the parenting schedule.'
            busy={busy || saving}
            onAnswer={answerQuestion}
          />
        ) : null}

        {screen === 'bankruptcy' ? (
          <QuestionScreen
            label='Is either of you in an active bankruptcy case?'
            busy={busy || saving}
            onAnswer={answerQuestion}
          />
        ) : null}

        {screen === 'pending_cases' ? (
          <QuestionScreen
            label='Are there any open family-law cases between you?'
            hint='Custody, protective orders, or an earlier divorce that is still open.'
            busy={busy || saving}
            onAnswer={answerQuestion}
          />
        ) : null}

        {screen === 'prior_marriage' ? (
          <QuestionScreen
            label='If either of you was married before, is that divorce finished?'
            hint='Answer yes if neither of you has a prior marriage, or if every prior divorce is final.'
            busy={busy || saving}
            onAnswer={answerQuestion}
          />
        ) : null}

        {screen === 'pregnant' ? (
          <QuestionScreen
            label='Is anyone currently pregnant?'
            busy={busy || saving}
            onAnswer={answerQuestion}
          />
        ) : null}

        {screen === 'outcome' ? (
          <OutcomeScreen
            gate={gate}
            emailAttached={emailAttached}
            onContinue={() => {
              track('Divorce Fit Check Continue Clicked');
              onContinue();
            }}
            onAttachEmail={onAttachEmail}
          />
        ) : null}
      </div>
    </div>
  );
}

function IntroScreen({ onStart }: { onStart: () => void }) {
  return (
    <div>
      <p className='text-sm font-semibold uppercase tracking-wide text-primary' style={{ ...nunito, fontWeight: 700 }}>
        Free fit check
      </p>
      <h1 className='mt-2 text-3xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        See if Harbor may fit, before you share names.
      </h1>
      <p className='mt-4 text-lg leading-relaxed text-gray-600' style={nunito}>
        A few yes-or-no questions. No court filing. If Harbor is not the right
        tool, we will say so and point you to better help.
      </p>
      <ul className='mt-6 space-y-3 text-gray-700' style={nunito}>
        {[
          'Takes a couple of minutes',
          'Your draft saves as you go',
          'You can stop any time',
        ].map((item) => (
          <li key={item} className='flex items-start gap-3'>
            <FiCheck className='mt-0.5 h-5 w-5 shrink-0 text-primary' />
            {item}
          </li>
        ))}
      </ul>
      <button
        type='button'
        onClick={onStart}
        className='mt-8 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-hover'
        style={{ ...nunito, fontWeight: 600 }}
      >
        Start the free check
        <FiArrowRight />
      </button>
      <p className='mt-4 text-xs text-gray-500' style={nunito}>
        {DOC_PREP_DISCLAIMER}
      </p>
    </div>
  );
}

function StateSelectScreen({
  initialCode,
  busy,
  onSelect,
}: {
  initialCode?: string;
  busy: boolean;
  onSelect: (code: string) => void;
}) {
  const [code, setCode] = useState(initialCode ?? '');

  return (
    <div>
      <h2 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        Which state will you file in?
      </h2>
      <p className='mt-3 text-gray-600' style={nunito}>
        Divorce is filed in the state where you or your spouse meet residency
        requirements, usually where you live now.
      </p>
      <select
        className='mt-6 w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900'
        style={nunito}
        value={code}
        onChange={(e) => setCode(e.target.value)}
      >
        <option value=''>Select your state…</option>
        {DIVORCE_JURISDICTIONS.map((j) => (
          <option key={j.code} value={j.code}>
            {j.name}
          </option>
        ))}
      </select>
      <button
        type='button'
        disabled={!code || busy}
        onClick={() => onSelect(code)}
        className='mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-hover disabled:opacity-50'
        style={{ ...nunito, fontWeight: 600 }}
      >
        Continue
        <FiArrowRight />
      </button>
    </div>
  );
}

function StateUnsupportedScreen({
  stateCode,
  emailAttached,
  onAttachEmail,
}: {
  stateCode: string;
  emailAttached: boolean;
  onAttachEmail: (email: string) => Promise<boolean>;
}) {
  const jurisdiction = getDivorceJurisdictionByCode(stateCode);
  const name = jurisdiction?.name ?? 'your state';

  return (
    <div>
      <h2 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        Harbor is not preparing forms in {name} yet.
      </h2>
      <p className='mt-3 text-gray-600' style={nunito}>
        Nothing has been filed. You can still file on your own, and Harbor&apos;s
        free {name} checklist walks through the official forms, fees, and steps.
      </p>
      {jurisdiction ? (
        <Link
          to='/divorce/$state'
          params={{ state: jurisdiction.slug }}
          className='mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-hover'
          style={{ ...nunito, fontWeight: 600 }}
        >
          See the free {name} checklist
          <FiArrowRight />
        </Link>
      ) : null}
      {emailAttached ? (
        <p className='mt-6 text-gray-700' style={nunito}>
          Your email is saved. Harbor will let you know when {name} opens.
        </p>
      ) : (
        <EmailCaptureForm
          prompt={`Optional: leave an email on a private inbox and Harbor will let you know when ${name} opens.`}
          buttonLabel='Tell me when my state opens'
          successText={`Saved. Harbor will email you when ${name} opens.`}
          onAttachEmail={onAttachEmail}
        />
      )}
      <Link
        to='/divorce'
        className='mt-6 inline-flex items-center justify-center text-sm font-semibold text-primary'
        style={{ ...nunito, fontWeight: 600 }}
      >
        Back to Harbor Divorce
      </Link>
    </div>
  );
}

function QuestionScreen({
  label,
  hint,
  busy,
  onAnswer,
}: {
  label: string;
  hint?: string;
  busy: boolean;
  onAnswer: (yes: boolean) => void;
}) {
  return (
    <div>
      <h2 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        {label}
      </h2>
      {hint ? (
        <p className='mt-3 text-gray-600' style={nunito}>
          {hint}
        </p>
      ) : null}
      <div className='mt-8 grid grid-cols-2 gap-3'>
        <button
          type='button'
          disabled={busy}
          onClick={() => onAnswer(true)}
          className='rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-hover disabled:opacity-50'
          style={{ ...nunito, fontWeight: 600 }}
        >
          Yes
        </button>
        <button
          type='button'
          disabled={busy}
          onClick={() => onAnswer(false)}
          className='rounded-lg border border-gray-300 px-6 py-3 font-semibold text-gray-800 transition-colors hover:bg-gray-50 disabled:opacity-50'
          style={{ ...nunito, fontWeight: 600 }}
        >
          No
        </button>
      </div>
    </div>
  );
}

function OutcomeScreen({
  gate,
  emailAttached,
  onContinue,
  onAttachEmail,
}: {
  gate: FitCheckGate | null;
  emailAttached: boolean;
  onContinue: () => void;
  onAttachEmail: (email: string) => Promise<boolean>;
}) {
  const status = gate?.status ?? 'eligible';

  if (status === 'blocked') {
    return (
      <div>
        <h2 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
          Harbor&apos;s guided process does not look like the right fit for this situation.
        </h2>
        <p className='mt-3 text-gray-600' style={nunito}>
          That is not a judgment on your case. Some situations need a different
          process or a licensed professional.
        </p>
        <div className='mt-6 space-y-4'>
          {(gate?.reasons ?? []).map((reason) => (
            <div key={reason.code} className='rounded-xl bg-gray-50 p-4'>
              <p className='text-gray-800' style={nunito}>
                {reason.message}
              </p>
              {reason.resource_url ? (
                <a
                  href={reason.resource_url}
                  className='mt-2 inline-block text-sm font-semibold text-primary'
                  target={reason.resource_url.startsWith('http') ? '_blank' : undefined}
                  rel='noreferrer'
                >
                  {reason.code === 'dv_safety'
                    ? 'National Domestic Violence Hotline'
                    : 'Find a professional'}
                </a>
              ) : null}
            </div>
          ))}
        </div>
        <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
          <Link
            to='/divorce'
            className='inline-flex items-center justify-center rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-white'
            style={{ ...nunito, fontWeight: 600 }}
          >
            See the free checklist
          </Link>
          <a
            href='/professionals'
            className='inline-flex items-center justify-center rounded-lg border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-800'
            style={{ ...nunito, fontWeight: 600 }}
          >
            Browse professionals
          </a>
        </div>
      </div>
    );
  }

  if (status === 'wait') {
    return (
      <WaitOutcome
        reasons={gate?.reasons ?? []}
        emailAttached={emailAttached}
        onAttachEmail={onAttachEmail}
      />
    );
  }

  return (
    <div>
      <h2 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        Harbor may fit your situation.
      </h2>
      <p className='mt-3 text-lg leading-relaxed text-gray-600' style={nunito}>
        Next you will answer the rest of the interview. Harbor prepares official
        California forms from those answers. You review every page. You file
        with the court. Pay only if you want the packet.
      </p>
      <button
        type='button'
        onClick={onContinue}
        className='mt-8 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white transition-colors hover:bg-primary-hover'
        style={{ ...nunito, fontWeight: 600 }}
      >
        Continue free
        <FiArrowRight />
      </button>
    </div>
  );
}

function WaitOutcome({
  reasons,
  emailAttached,
  onAttachEmail,
}: {
  reasons: FitCheckGate['reasons'];
  emailAttached: boolean;
  onAttachEmail: (email: string) => Promise<boolean>;
}) {
  return (
    <div>
      <h2 className='text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
        You may be able to use Harbor later.
      </h2>
      <p className='mt-3 text-gray-600' style={nunito}>
        Your answers are saved. Nothing has been filed.
      </p>
      <div className='mt-6 space-y-4'>
        {reasons.map((reason) => (
          <div key={reason.code} className='rounded-xl bg-gray-50 p-4'>
            <p className='text-gray-800' style={nunito}>
              {reason.message}
            </p>
          </div>
        ))}
      </div>
      {emailAttached ? (
        <p className='mt-6 text-gray-700' style={nunito}>
          We will keep this draft. If you left an email on a private inbox, that
          is how you can come back.
        </p>
      ) : (
        <EmailCaptureForm
          prompt='Optional: email a resume link. Harbor only emails inboxes that are private to you.'
          buttonLabel='Email me my resume link'
          successText='We will keep this draft. If you left an email on a private inbox, that is how you can come back.'
          onAttachEmail={onAttachEmail}
        />
      )}
      <Link
        to='/divorce'
        className='mt-6 inline-flex items-center justify-center text-sm font-semibold text-primary'
        style={{ ...nunito, fontWeight: 600 }}
      >
        Back to Harbor Divorce
      </Link>
    </div>
  );
}

function EmailCaptureForm({
  prompt,
  buttonLabel,
  successText,
  onAttachEmail,
}: {
  prompt: string;
  buttonLabel: string;
  successText: string;
  onAttachEmail: (email: string) => Promise<boolean>;
}) {
  const [email, setEmail] = useState('');
  const [inboxPrivate, setInboxPrivate] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!inboxPrivate || status === 'loading') return;
    setStatus('loading');
    const ok = await onAttachEmail(email);
    setStatus(ok ? 'success' : 'error');
  };

  if (status === 'success') {
    return (
      <p className='mt-6 text-gray-700' style={nunito}>
        {successText}
      </p>
    );
  }

  return (
    <form onSubmit={submit} className='mt-8 space-y-4'>
      <p className='text-sm text-gray-600' style={nunito}>
        {prompt}
      </p>
      <input
        type='email'
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder='you@example.com'
        className='w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900'
        style={nunito}
      />
      <label className='flex items-start gap-3 text-sm text-gray-700' style={nunito}>
        <input
          type='checkbox'
          checked={inboxPrivate}
          onChange={(e) => setInboxPrivate(e.target.checked)}
          className='mt-1'
        />
        <span>My email inbox is private to me. Harbor never emails shared inboxes.</span>
      </label>
      <button
        type='submit'
        disabled={!inboxPrivate || status === 'loading'}
        className='w-full rounded-lg bg-primary py-3 font-semibold text-white disabled:opacity-50'
        style={{ ...nunito, fontWeight: 600 }}
      >
        {status === 'loading' ? 'Saving…' : buttonLabel}
      </button>
      {status === 'error' ? (
        <p className='text-sm font-medium text-red-600' style={nunito}>
          We could not save that email. Your draft is still here if you keep this link.
        </p>
      ) : null}
    </form>
  );
}
